import * as RadixDropdown from "@radix-ui/react-dropdown-menu";
import * as RadixPopover from "@radix-ui/react-popover";
import { Check, ChevronRight, type LucideIcon, MoreHorizontal } from "lucide-react";
import type { ReactNode } from "react";
import { Icon } from "../icons/Icon";
import { cx } from "../styles";
import { IconButton } from "./Button";
import { Kbd } from "./Display";

/**
 * A popover and a dropdown menu, which look alike and are not the same thing.
 *
 * A **popover** holds content: a filter form, a detail card, a date picker. It
 * does not trap focus, and Tab moves through it and out the other side.
 *
 * A **menu** holds actions. It traps focus, moves with arrows rather than Tab,
 * answers to type-ahead, and closes when an item is chosen. Putting a form
 * inside one is how a text field ends up unusable, because every keystroke is
 * read as type-ahead.
 */

/**
 * The floating surface both of them sit on.
 *
 * `--z-popover`, above `--z-overlay`: a menu opened from inside a drawer has to
 * land on top of that drawer (ADJ-M03-1).
 */
export const SURFACE =
	"z-[var(--z-popover)] rounded-lg border border-border bg-bg-raised shadow-overlay " +
	"data-[state=open]:animate-[km-rise_150ms_ease-out] " +
	"motion-reduce:!animate-none";

export interface PopoverProps {
	trigger: ReactNode;
	children: ReactNode;
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	align?: "start" | "center" | "end";
	side?: "top" | "right" | "bottom" | "left";
	className?: string;
}

export function Popover({
	trigger,
	children,
	open,
	onOpenChange,
	align = "start",
	side = "bottom",
	className,
}: PopoverProps) {
	return (
		<RadixPopover.Root
			{...(open === undefined ? {} : { open })}
			{...(onOpenChange === undefined ? {} : { onOpenChange })}
		>
			<RadixPopover.Trigger asChild>{trigger}</RadixPopover.Trigger>
			<RadixPopover.Portal>
				<RadixPopover.Content
					// `align="start"` means the start of the *inline* axis, so a popover
					// aligned under the start of its trigger stays under it in Arabic.
					// Radix handles the flip; nothing here has to.
					align={align}
					side={side}
					sideOffset={6}
					collisionPadding={8}
					className={cx(SURFACE, "p-3", className)}
				>
					{children}
				</RadixPopover.Content>
			</RadixPopover.Portal>
		</RadixPopover.Root>
	);
}

export interface MenuItem {
	/** A stable id, used as the React key and returned by `onSelect`. */
	id: string;
	label: ReactNode;
	icon?: LucideIcon;
	/** A keyboard shortcut, rendered as a key cap at the inline end. */
	shortcut?: string;
	/** Draws the item in the danger tone. For destructive actions only. */
	danger?: boolean;
	disabled?: boolean;
	onSelect?: () => void;
	/** Nested items. The submenu opens towards the inline end and flips in RTL. */
	items?: MenuItem[];
	/**
	 * Renders the item as a choice with a state: a tick when it is the current
	 * one, an empty gutter when it is not.
	 *
	 * A checkbox item rather than a radio one, even for a set where exactly one
	 * is current. Radix's radio group takes its value from a wrapper around the
	 * items, which would mean a group shape in {@link DropdownMenuProps} that
	 * every caller pays for and two use — and a screen reader announces
	 * "Dark, checked" either way, which is the part that matters. The gutter is
	 * reserved on every item in the group so the labels do not shift when the
	 * tick moves.
	 */
	checked?: boolean;
}

export interface DropdownMenuProps {
	trigger: ReactNode;
	/** Groups, separated by a rule. An empty group is dropped rather than drawn. */
	groups: MenuItem[][];
	align?: "start" | "center" | "end";
	/** Names the menu for assistive technology when the trigger is icon-only. */
	label?: string;
}

const ITEM =
	"flex cursor-pointer select-none items-center gap-2 rounded-sm px-2 py-1.5 text-[13px] " +
	"outline-none data-[highlighted]:bg-brand-subtle data-[highlighted]:text-brand-ink " +
	"data-[disabled]:cursor-not-allowed data-[disabled]:text-fg-subtle max-sm:min-h-12";

/**
 * The document's direction, for Radix. There is no DirectionProvider in the
 * apps, and without one Radix assumes left-to-right: in Arabic a submenu
 * opened on the wrong side and the arrow that opens it was the wrong arrow.
 * Read when the menu renders, so a locale switch is picked up on the next open.
 */
function documentDirection(): "ltr" | "rtl" {
	return typeof document !== "undefined" && document.documentElement.dir === "rtl" ? "rtl" : "ltr";
}

export function DropdownMenu({ trigger, groups, align = "end", label }: DropdownMenuProps) {
	const populated = groups.filter((group) => group.length > 0);

	return (
		// `modal={false}` (ADJ-M03-1). A modal menu sets `pointer-events: none`
		// on <body> while it is open, and an item that opens a dialog or a
		// drawer from `onSelect` races that clean-up with the dialog's own: the
		// body was left unclickable after the dialog closed, and the whole page
		// stopped answering clicks. Non-modal keeps every menu behaviour that
		// matters — Escape, outside click, arrows, type-ahead, focus returned to
		// the trigger — without touching the body.
		<RadixDropdown.Root dir={documentDirection()} modal={false}>
			<RadixDropdown.Trigger asChild>{trigger}</RadixDropdown.Trigger>
			<RadixDropdown.Portal>
				<RadixDropdown.Content
					align={align}
					sideOffset={6}
					collisionPadding={8}
					aria-label={label}
					className={cx(SURFACE, "min-w-44 p-1")}
				>
					{populated.map((group, index) => (
						// The group's identity is its first item, which is stable; the
						// index is only the tiebreaker for a one-item group repeated.
						<div key={group[0]?.id ?? index}>
							{index > 0 && <RadixDropdown.Separator className="my-1 h-px bg-border" />}
							{group.map((item) => (
								<MenuEntry key={item.id} item={item} />
							))}
						</div>
					))}
				</RadixDropdown.Content>
			</RadixDropdown.Portal>
		</RadixDropdown.Root>
	);
}

export interface ActionsMenuProps {
	/** Names the trigger and the menu: "Actions for SKU-1042". */
	label: string;
	/** One group, or several separated by a rule. Empty groups are dropped. */
	items: MenuItem[] | MenuItem[][];
	/** The trigger's glyph. Three dots unless the row has a better word for it. */
	icon?: LucideIcon;
	size?: "sm" | "md";
}

/**
 * The "three dots" row or card menu, in one place (ADJ-M03-1).
 *
 * A {@link DropdownMenu} behind a ghost icon button: portalled, so no table or
 * card with `overflow` can clip it; flipped and shifted by Radix to stay in the
 * viewport, including above the trigger near the bottom of the screen; keyboard
 * operable; and aligned to the inline end, so it opens towards the page in
 * Arabic too. Renders nothing when there is nothing to offer, which is what a
 * row the caller may not act on should show.
 */
export function ActionsMenu({
	label,
	items,
	icon = MoreHorizontal,
	size = "sm",
}: ActionsMenuProps) {
	const groups = (items.length > 0 && Array.isArray(items[0]) ? items : [items]) as MenuItem[][];
	if (groups.every((group) => group.length === 0)) {
		return null;
	}
	return (
		<DropdownMenu
			label={label}
			groups={groups}
			trigger={<IconButton icon={icon} size={size} variant="ghost" label={label} />}
		/>
	);
}

function MenuEntry({ item }: { item: MenuItem }) {
	const tone = item.danger === true ? "text-danger-fg" : "text-fg";

	if (item.items !== undefined && item.items.length > 0) {
		return (
			<RadixDropdown.Sub>
				<RadixDropdown.SubTrigger
					className={cx(ITEM, tone)}
					{...(item.disabled === undefined ? {} : { disabled: item.disabled })}
				>
					{item.icon && <Icon as={item.icon} size={14} />}
					<span className="flex-1">{item.label}</span>
					{/* Points towards the submenu, which opens at the inline end — so it
					    points left in Arabic, and Radix opens the panel there too. */}
					<Icon as={ChevronRight} size={14} mirror className="text-fg-subtle" />
				</RadixDropdown.SubTrigger>
				<RadixDropdown.Portal>
					<RadixDropdown.SubContent sideOffset={4} className={cx(SURFACE, "min-w-44 p-1")}>
						{item.items.map((child) => (
							<MenuEntry key={child.id} item={child} />
						))}
					</RadixDropdown.SubContent>
				</RadixDropdown.Portal>
			</RadixDropdown.Sub>
		);
	}

	if (item.checked !== undefined) {
		return (
			<RadixDropdown.CheckboxItem
				className={cx(ITEM, tone)}
				checked={item.checked}
				{...(item.disabled === undefined ? {} : { disabled: item.disabled })}
				// Radix closes on select, which is right: a menu is a list of
				// actions, and choosing a theme is one.
				{...(item.onSelect === undefined ? {} : { onSelect: item.onSelect })}
			>
				<span className="grid w-3.5 shrink-0 place-items-center">
					<RadixDropdown.ItemIndicator>
						<Icon as={Check} size={14} />
					</RadixDropdown.ItemIndicator>
				</span>
				<span className="flex-1">{item.label}</span>
			</RadixDropdown.CheckboxItem>
		);
	}

	return (
		<RadixDropdown.Item
			className={cx(ITEM, tone)}
			// Spread rather than passed: Radix types both of these as required-if-
			// present, and `exactOptionalPropertyTypes` will not let an `undefined`
			// through a prop whose type does not include it.
			{...(item.disabled === undefined ? {} : { disabled: item.disabled })}
			{...(item.onSelect === undefined ? {} : { onSelect: item.onSelect })}
		>
			{item.icon && <Icon as={item.icon} size={14} />}
			<span className="flex-1">{item.label}</span>
			{item.shortcut !== undefined && <Kbd>{item.shortcut}</Kbd>}
		</RadixDropdown.Item>
	);
}
